<template>
  <div style="overflow-x:hidden;">

    <!-- ═══ PAGE HERO ═══ -->
    <div class="nf-page-hero">
      <h1>Our Services</h1>
      <ul class="nf-breadcrumb">
        <li><router-link to="/">Home</router-link></li>
        <li class="sep"><i class="fas fa-chevron-right" style="font-size:.65rem;"></i></li>
        <li class="active">Services</li>
      </ul>
    </div>

    <!-- ═══ SERVICES GRID ═══ -->
    <section class="py-6 px-3 px-lg-5 nf-section-lines">
      <div class="container-fluid">
        <div class="text-center mb-5 nf-reveal">
          <span class="section-label">What We Offer</span>
          <h2 class="section-title">Our Services</h2>
          <hr class="section-divider" />
          <p class="text-muted mx-auto" style="max-width:600px;">
            Innovative, impact-driven solutions across sustainability, engineering, and community development.
          </p>
        </div>

        <!-- Mobile accordion -->
        <div class="accordion d-md-none mb-4" id="servicesAccordion">
          <div class="accordion-item border-0 mb-3 rounded-3 overflow-hidden shadow-sm" v-for="(svc, i) in serviceItems" :key="i">
            <h2 class="accordion-header">
              <button
                class="accordion-button collapsed rounded-3"
                type="button"
                :data-bs-toggle="'collapse'"
                :data-bs-target="'#svcCollapse'+i"
              >
                <i :class="svc.icon + ' me-2'" :style="{color: svc.color}"></i>
                {{ svc.title }}
              </button>
            </h2>
            <div :id="'svcCollapse'+i" class="accordion-collapse collapse" :data-bs-parent="'#servicesAccordion'">
              <div class="accordion-body text-muted">{{ svc.desc }}</div>
            </div>
          </div>
        </div>

        <!-- Desktop grid -->
        <div class="row g-4 d-none d-md-flex nf-stagger">
          <div class="col-md-6 col-lg-4" v-for="(svc, i) in serviceItems" :key="i">
            <div class="nf-service-card">
              <div class="nf-service-icon" :style="{ background: svc.bg }">
                <i :class="svc.icon" :style="{ color: svc.color }"></i>
              </div>
              <h5 class="mb-2">{{ svc.title }}</h5>
              <p class="small text-muted mb-0">{{ svc.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══ REQUEST A SERVICE ═══ -->
    <section class="py-6 px-3 px-lg-5 nf-section-dots">
      <div class="container-fluid">
        <div class="text-center mb-5 nf-reveal">
          <span class="section-label">Work With Us</span>
          <h2 class="section-title">{{ quoteContent.title || 'Request a Service' }}</h2>
          <hr class="section-divider" />
        </div>

        <div class="row g-5 align-items-center justify-content-center">
          <!-- Form -->
          <div class="col-12 col-lg-6 nf-reveal-left">
            <div class="nf-form-panel">
              <h4 class="mb-1">{{ quoteContent.form_title || 'Tell us what you need' }}</h4>
              <p class="text-muted small mb-4">{{ quoteContent.form_description || "Fill in the form and we'll be in touch." }}</p>

              <form @submit.prevent="submitQuote">
                <div class="row g-3">
                  <div class="col-12 col-sm-6">
                    <div class="form-floating">
                      <input type="text" class="form-control" id="svc-name" placeholder="John Doe" v-model="fullName" required />
                      <label for="svc-name">Full Name</label>
                    </div>
                  </div>
                  <div class="col-12 col-sm-6">
                    <div class="form-floating">
                      <input type="email" class="form-control" id="svc-email" placeholder="email@example.com" v-model="email" required />
                      <label for="svc-email">Email Address</label>
                    </div>
                  </div>
                  <div class="col-12 col-sm-6">
                    <div class="form-floating">
                      <select class="form-select" id="svc-select" v-model="selectedServiceId"
                        :disabled="customServiceName.trim() !== ''"
                        :required="customServiceName.trim() === ''">
                        <option disabled value="">Choose a service</option>
                        <option v-for="s in services" :key="s.id" :value="s.id">{{ s.name }}</option>
                      </select>
                      <label for="svc-select">Select a Service</label>
                    </div>
                  </div>
                  <div class="col-12 col-sm-6">
                    <div class="form-floating">
                      <textarea class="form-control" id="svc-custom" placeholder="Custom service"
                        v-model="customServiceName"
                        :disabled="selectedServiceId !== ''"
                        style="height:100px;"></textarea>
                      <label for="svc-custom">Or Describe Your Need</label>
                    </div>
                  </div>
                  <div class="col-12">
                    <button class="btn btn-primary w-100 py-3 rounded-pill" type="submit" :disabled="loading">
                      <span v-if="!loading"><i class="fas fa-paper-plane me-2"></i>Send Request</span>
                      <span v-else><i class="fas fa-spinner fa-spin me-2"></i>Sending…</span>
                    </button>
                  </div>
                </div>
              </form>

              <div v-if="message" class="mt-3 p-3 rounded-3 small fw-semibold"
                :class="success ? 'nf-alert-success' : 'nf-alert-error'">
                <i :class="success ? 'fas fa-check-circle me-2' : 'fas fa-exclamation-circle me-2'"></i>{{ message }}
              </div>
            </div>
          </div>

          <!-- Image -->
          <div class="col-12 col-lg-5 d-none d-lg-block nf-reveal-right">
            <img :src="'/img/quote.jpg'" class="img-fluid rounded-4 shadow-lg" alt="Consultation" style="object-fit:cover;max-height:480px;width:100%;" />
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
export default {
  name: 'ServicesView',
  data() {
    return {
      services: [],
      quoteContent: {},
      fullName: '',
      email: '',
      selectedServiceId: '',
      customServiceName: '',
      message: '',
      success: false,
      loading: false,
      serviceItems: [
        { icon:'fa fa-search',        title:'Research & Technical Studies',  desc:'Vulnerability assessments, EIA, GIS mapping, and technical policy research for climate and environmental analysis.', bg:'#e7f4ed', color:'#1a6b4a' },
        { icon:'fa fa-leaf',          title:'Climate & Resource Management', desc:'Adaptation planning, biodiversity conservation, renewable energy and carbon credit initiatives.', bg:'#e8f8f0', color:'#1a7a4a' },
        { icon:'fa fa-chart-line',    title:'ESG & Sustainability',          desc:'ESG risk analysis, sustainability reporting, and SDG integration in governance and corporate strategy.', bg:'#fdf4e0', color:'#c9992d' },
        { icon:'fa fa-tools',         title:'Engineering & Project Support', desc:'From feasibility studies and design to construction supervision and civil engineering oversight.', bg:'#fce8e8', color:'#c0392b' },
        { icon:'fa fa-users',         title:'Community Development & DRR',  desc:'Supporting communities through livelihoods programmes, DRR, gender inclusion, and essential services.', bg:'#e8f0fc', color:'#2563eb' },
        { icon:'fa fa-graduation-cap',title:'Education & Capacity Building', desc:'OHS training, awareness campaigns, environmental education, and emergency preparedness programmes.', bg:'#f4e7fd', color:'#7c3aed' },
      ],
    };
  },
  async mounted() {
    try {
      const [svcRes, contentRes] = await Promise.all([
        fetch('https://admin.nexusfocusconsultancy.com/quote/api/services/'),
        fetch('https://admin.nexusfocusconsultancy.com/quote/api/quote-content/'),
      ]);
      this.services    = await svcRes.json();
      this.quoteContent = await contentRes.json();
    } catch (e) {
      this.message = 'Failed to load content.';
    }
  },
  methods: {
    async submitQuote() {
      this.loading = true;
      this.message = '';
      this.success = false;
      if (!this.selectedServiceId && !this.customServiceName.trim()) {
        this.message = 'Please select a service or describe your need.';
        this.loading = false;
        return;
      }
      try {
        const payload = { full_name: this.fullName, email: this.email };
        if (this.customServiceName.trim()) payload.custom_service_name = this.customServiceName.trim();
        else payload.service_id = this.selectedServiceId;

        const res = await fetch('https://admin.nexusfocusconsultancy.com/quote/api/quote/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || 'Submission failed.');
        }
        this.message = 'Your request was sent successfully. We\'ll be in touch soon!';
        this.success = true;
        this.fullName = ''; this.email = ''; this.selectedServiceId = ''; this.customServiceName = '';
      } catch (e) {
        this.message = e.message;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.nf-alert-success { background:#e7f4ed; color:#1a6b4a; }
.nf-alert-error   { background:#fce8e8; color:#c0392b; }
</style>
